import type { ZodError } from 'zod';
import type {
  ContentTypeDefinition,
  ContentTypePropertyConfig,
  ContentTypePropertySchema,
} from '../../../lib/flotiq-client/types/api-response';
import type { ContentTypeFormProperty } from '../../PropertyModalForm/helpers/parseProperty';
import type { DraggableCardEditFn } from '../DraggableCard/DraggableCard';

import { useStore } from '@tanstack/react-form';
import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { deepReadKeyValue, getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import HelpErrorTextsTemplate from '../../../components/HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import Tooltip from '../../../components/Tooltip/Tooltip';

import useDebounceCallback from '../../../hooks/useDebounceCallback';

import ContentTypeFormContext from '../../../contexts/ContentTypeFormContext';
import ListPropertiesContext from '../../../contexts/ListPropertiesContext';
import { useModals } from '../../../contexts/ModalContext';
import PropertyModalCtdsContext from '../../../contexts/PropertyModalCtdsContext';

import { CaretUpIcon, PlusCircleOutlineIcon } from '../../../images/shapes';

import { withFieldGroup } from '../../../form-hooks/form';
import { EDITABLE } from '../../PropertyModalForm/helpers/propertiesFields';
import { validateListField } from '../../PropertyModalForm/helpers/validators';
import PropertyModalForm from '../../PropertyModalForm/PropertyModalForm';
import DraggableCard from '../DraggableCard/DraggableCard';
import {
  changeOrderDown,
  changeOrderUp,
  deleteProperty,
  updateOrder,
  updateProperties,
} from '../helpers/parser';

type ListPropertiesProps = {
  /**
   * List field property key
   */
  listName: string;
  /**
   * List items meta config
   */
  listPropertiesItems?: ContentTypePropertyConfig['items'];
  /**
   * List items schema
   */
  listSchemaItems?: ContentTypePropertySchema['items'];
  /**
   * If list items are open
   */
  isOpen?: boolean;
  /**
   * Additional classes for the list properties container
   */
  additionalClasses?: string;
  /**
   * Name of the parent field
   */
  parentInputType?: string;
  /**
   * Component test ID
   */
  testId?: string;
};

const ListProperties = withFieldGroup({
  defaultValues: {} as
    | {
        metaDefinition: ContentTypeDefinition['metaDefinition'];
        schemaDefinition: ContentTypeDefinition['schemaDefinition'];
      }
    | ContentTypeFormProperty,
  props: {} as ListPropertiesProps,
  render: function Render({
    group,
    listName,
    listPropertiesItems,
    listSchemaItems,
    isOpen = false,
    additionalClasses,
    testId,
    parentInputType,
  }) {
    const { t } = useTranslation();
    const modal = useModals();
    const [open, setOpen] = useState(isOpen);
    const ctdContext = use(ContentTypeFormContext);
    const { oldCtd, isEditing, setDirty } = ctdContext;
    const { highListName } = use(ListPropertiesContext);
    const propertyContext = use(PropertyModalCtdsContext);

    const errors = useStore(group.form.store, ({ fieldMeta }) => {
      const meta = fieldMeta?.[listName];
      return meta?.isTouched
        ? meta?.errors?.[0]?.map((error: string | ZodError) => {
            errors.add(typeof error === 'string' ? error : error.message);
          })
        : [];
    });

    const isInPropertyModal = useMemo(
      () => Object.keys(propertyContext).length > 0,
      [propertyContext],
    );

    const listPropertiesContextValue = useMemo(
      () => ({
        highListName: listName,
        isNestedList: !!highListName,
      }),
      [highListName, listName],
    );

    const metaPath = useMemo(() => {
      const pathEnd = highListName ? `.propertiesConfig.${listName}.items` : '';

      if (isInPropertyModal) {
        if (
          parentInputType === 'edit_object_object' ||
          parentInputType === 'edit_undefined_object'
        ) {
          return `config.items`;
        }

        return `config.items` + pathEnd;
      }
      return (
        `metaDefinition.propertiesConfig.${
          highListName ? highListName : listName
        }.items` + pathEnd
      );
    }, [highListName, isInPropertyModal, listName, parentInputType]);

    const schemaPath = useMemo(() => {
      const pathEnd = highListName ? `.properties.${listName}.items` : '';

      if (isInPropertyModal) {
        if (
          parentInputType === 'edit_object_object' ||
          parentInputType === 'edit_undefined_object'
        ) {
          return `schema.items`;
        }

        return `schema.items` + pathEnd;
      }

      return (
        `schemaDefinition.allOf[1].properties.${
          highListName ? highListName : listName
        }.items` + pathEnd
      );
    }, [highListName, isInPropertyModal, listName, parentInputType]);

    const oldOrder = useMemo(
      () => (deepReadKeyValue(`${metaPath}.order`, oldCtd) as string[]) || [],
      [metaPath, oldCtd],
    );

    useEffect(() => {
      if (
        !listSchemaItems?.properties ||
        Array.isArray(listSchemaItems?.properties)
      ) {
        group.form.setFieldValue(`${schemaPath}.properties`, {});
      }
      if (
        !listPropertiesItems?.propertiesConfig ||
        Array.isArray(listPropertiesItems?.propertiesConfig)
      ) {
        group.form.setFieldValue(`${metaPath}.propertiesConfig`, {});
      }
    }, [
      group.form,
      listPropertiesItems?.propertiesConfig,
      listSchemaItems?.properties,
      metaPath,
      schemaPath,
    ]);

    const handleDeleteField = useCallback(
      async (fieldName: string) => {
        const showModal = isEditing && oldOrder.indexOf(fieldName) > -1;

        const result = !showModal
          ? true
          : await modal.delete(t('ContentTypeForm.DeleteProperty'));

        if (result) {
          deleteProperty(
            group,
            fieldName,
            metaPath,
            schemaPath,
            schemaPath,
            setDirty,
          );
        }
      },
      [isEditing, oldOrder, modal, t, group, metaPath, schemaPath, setDirty],
    );

    const handleChangeOrder = useCallback(
      (sourceName: string, targetName: string) => {
        updateOrder(sourceName, targetName, group, metaPath, setDirty);
      },
      [group, metaPath, setDirty],
    );

    const onDropCallback = useDebounceCallback(handleChangeOrder, 10);

    const handleOrderUp = useCallback(
      (idx: number) => {
        changeOrderUp(group, metaPath, idx, setDirty);
      },
      [group, metaPath, setDirty],
    );

    const handleOrderDown = useCallback(
      (idx: number) => {
        changeOrderDown(group, metaPath, idx, setDirty);
      },
      [group, metaPath, setDirty],
    );

    const updateFormProperties = useCallback(
      (
        property: ContentTypeFormProperty | undefined,
        newProperty: ContentTypeFormProperty,
        copiedPropertyIdx?: number,
      ) => {
        updateProperties(
          group,
          property,
          newProperty,
          copiedPropertyIdx,
          metaPath,
          schemaPath,
          schemaPath,
          setDirty,
        );
      },
      [group, metaPath, schemaPath, setDirty],
    );

    const openPropertyModal = useCallback<DraggableCardEditFn>(
      async (
        fieldName,
        fieldProps,
        fieldSchema,
        isRequired,
        copiedPropertyIdx,
        parentInputType,
      ) => {
        let property: ContentTypeFormProperty | undefined = undefined;
        if (fieldName && fieldProps && fieldSchema) {
          property = {
            key: fieldName,
            config: fieldProps,
            schema: fieldSchema,
            required: !!isRequired,
          };
        }

        const newProperty = await modal({
          title: (
            <div className="font-bold text-xl sm:text-3xl">
              {t('ContentTypeForm.ContentProperty')}
            </div>
          ),
          content: (
            <ContentTypeFormContext value={ctdContext}>
              <ListPropertiesContext value={listPropertiesContextValue}>
                <PropertyModalForm
                  property={property}
                  order={listPropertiesItems?.order}
                  isNew={
                    (copiedPropertyIdx != null && copiedPropertyIdx > -1) ||
                    (!!property?.key && oldOrder.indexOf(property.key) < 0)
                  }
                  isDuplicate={
                    copiedPropertyIdx != null && copiedPropertyIdx > -1
                  }
                  parentInputType={`edit_${parentInputType}`}
                  {...getTestProps(testId, 'property-form', 'testId')}
                />
              </ListPropertiesContext>
            </ContentTypeFormContext>
          ),
          size: '2xl',
          dialogAdditionalClasses: 'h-full lg:max-h-[calc(100vh-2rem)]',
        });

        if (!newProperty) return;

        updateFormProperties(
          copiedPropertyIdx != null && copiedPropertyIdx > -1
            ? undefined
            : property,
          newProperty,
          copiedPropertyIdx,
        );

        group.form.validateField('config.items.order', 'change');
      },
      [
        modal,
        t,
        ctdContext,
        listPropertiesContextValue,
        listPropertiesItems?.order,
        oldOrder,
        testId,
        updateFormProperties,
        group.form,
      ],
    );

    const handleEditField = useCallback<DraggableCardEditFn>(
      async (
        fieldName,
        fieldProps,
        fieldSchema,
        isRequired,
        copiedPropertyIdx = -1,
        parentInputType,
      ) => {
        if (
          fieldProps?.inputType &&
          EDITABLE.indexOf(fieldProps.inputType) < 0
        ) {
          alert('Not implemented');
          return;
        }

        await openPropertyModal(
          fieldName,
          fieldProps,
          fieldSchema,
          isRequired,
          copiedPropertyIdx,
          parentInputType,
        );
      },
      [openPropertyModal],
    );

    const renderField = useCallback(
      (
        fieldName: string,
        idx: number,
        currentOrder: string[],
        parentInputType?: string,
      ) => (
        <DraggableCard
          key={fieldName}
          form={group}
          fields={
            isInPropertyModal
              ? {
                  key: 'key',
                  required: 'required',
                  config: 'config',
                  schema: 'schema',
                }
              : {
                  metaDefinition: 'metaDefinition',
                  schemaDefinition: 'schemaDefinition',
                }
          }
          fieldName={fieldName}
          idx={idx}
          currentOrder={currentOrder}
          parentInputType={parentInputType}
          fieldProps={listPropertiesItems?.propertiesConfig?.[fieldName]}
          fieldSchema={listSchemaItems?.properties?.[fieldName]}
          isRequired={
            listSchemaItems?.required &&
            listSchemaItems?.required?.indexOf(fieldName) > -1
          }
          onDelete={handleDeleteField}
          onEdit={handleEditField}
          onDrop={onDropCallback}
          onUp={handleOrderUp}
          onDown={handleOrderDown}
          onDuplicate={handleEditField}
          additionalContainerClasses="bg-slate-400/10 border border-slate-200"
          testId={testId}
        />
      ),
      [
        group,
        isInPropertyModal,
        listPropertiesItems?.propertiesConfig,
        listSchemaItems?.properties,
        listSchemaItems?.required,
        handleDeleteField,
        handleEditField,
        onDropCallback,
        handleOrderUp,
        handleOrderDown,
        testId,
      ],
    );

    return (
      <div className={twMerge('space-y-3 px-4 pb-4', additionalClasses)}>
        <div className="flex justify-end">
          <Button
            onClick={() => setOpen(!open)}
            buttonColor="borderless"
            iconImage={
              <CaretUpIcon
                className={twMerge(
                  'w-2 rotate-180 text-blue',
                  open ? 'rotate-0' : 'rotate-180',
                )}
              />
            }
            additionalIconClasses="ml-1"
            additionalClasses="mr-2"
            {...getTestProps(testId, 'show-properties', 'testId')}
          >
            {t('ContentTypeForm.ShowProperties')}
          </Button>
        </div>

        {/**
         * There are problems that main validation has no actual data of this field,
         * due to the fact that it is set programmatically.
         */}
        {isInPropertyModal && (
          <group.AppField
            name="config.items.order"
            validators={{
              onChange: ({ value, fieldApi }) => {
                if (!value) return;
                return validateListField(t, value, fieldApi.form);
              },
            }}
          >
            {(subField) => (
              <subField.ErrorField
                {...getTestProps(testId, 'order-error', 'testId')}
              />
            )}
          </group.AppField>
        )}

        <ListPropertiesContext value={listPropertiesContextValue}>
          {open &&
            (listPropertiesItems?.order || []).map(
              (fieldName, idx, currentOrder) =>
                renderField(fieldName, idx, currentOrder, parentInputType),
            )}
        </ListPropertiesContext>
        <Button
          onClick={() => openPropertyModal()}
          buttonColor="blueBordered"
          iconImage={
            <PlusCircleOutlineIcon className="w-5 text-blue group-hover:text-white" />
          }
          iconPosition="end"
          additionalClasses="border group mt-3.5!"
          additionalChildrenClasses="font-medium"
          disabled={!listName}
          {...getTestProps(testId, 'add-property', 'testId')}
        >
          {listName ? (
            t('ContentTypeForm.AddProperty')
          ) : (
            <Tooltip
              tooltip={t('ContentTypeForm.NameMissing')}
              tooltipPlacement="rightCenter"
            >
              {t('ContentTypeForm.AddProperty')}
            </Tooltip>
          )}
        </Button>
        <HelpErrorTextsTemplate
          error={errors}
          {...getTestProps(testId, 'items', 'testId')}
        />
      </div>
    );
  },
});

export default ListProperties;
